import type { ReactNode } from 'react';
import { Icon } from '../../icons/Icon';
import type { IconName } from '../../icons/registry';
import styles from './EmptyState.module.css';

export interface EmptyStateProps {
    /** Defaults to the `empty` concept. */
    readonly icon?: IconName;
    /** One line saying why this is empty — sentence case, no trailing period (§9). */
    readonly message: string;
    /**
     * The primary action, required. §5 specifies "icon + one-line explanation + primary action";
     * making the slot optional is how the action quietly stops shipping, which leaves the user
     * reading an explanation with nothing to do about it.
     */
    readonly action: ReactNode;
    readonly 'data-testid'?: string;
}

/**
 * §5's empty state. A shell around a caller-supplied action rather than an `onAction` callback: the
 * action is usually a Button, and Button already owns the whole interaction-state matrix. Taking a
 * label and a handler here would mean re-deriving variant, size, busy and disabled behaviour a
 * second time, and the two would drift.
 */
export function EmptyState({ icon = 'empty', message, action, 'data-testid': testId }: EmptyStateProps) {
    return (
        <div className={styles.empty} data-testid={testId}>
            <span className={styles.glyph}>
                <Icon emphasis="regular" name={icon} size="md" />
            </span>
            <p className={styles.message}>{message}</p>
            {action}
        </div>
    );
}
